import { ApiError, api } from '@/lib/api';
import type { PublicUser } from '@innermate/shared';
import { queryOptions, useQuery, useQueryClient } from '@tanstack/react-query';
import { type ReactNode, createContext, useCallback, useContext, useMemo } from 'react';

/**
 * `me` 쿼리 옵션. `useAuth` 와 router 의 `beforeLoad` 가드 (예: /wikis) 가 동일한
 * 캐시 키 + 동일한 queryFn 으로 데이터를 공유한다. 이로써 보호 라우트 진입 시
 * `queryClient.ensureQueryData(meQueryOptions)` 로 mount 이전에 인증 확정 가능 —
 * 컴포넌트 mount 후 useEffect 로 튕기는 race 가 사라진다.
 */
export const meQueryOptions = queryOptions({
  queryKey: ['auth', 'me'] as const,
  queryFn: async (): Promise<PublicUser | null> => {
    try {
      const res = await api.me();
      return res.user;
    } catch (err) {
      if (err instanceof ApiError && err.status === 401) return null;
      throw err;
    }
  },
  staleTime: 60_000,
  retry: false,
  refetchOnWindowFocus: false,
});

/**
 * `login` 의 두 번째 인자로 활성 인증 provider 를 받아 라우트를 분기.
 * - 'local' (또는 생략): `/api/auth/login` — 로컬 password / break-glass
 * - 'ldap': `/api/auth/ldap/login` — MS 패턴과 동일한 provider-별 라우트
 *
 * MS / SSO 는 OAuth redirect 로 별도 흐름이라 이 함수가 아닌 `/api/auth/<provider>/start`
 * 링크를 통해 진입.
 */
type LoginMode = 'local' | 'ldap';

interface AuthContextValue {
  user: PublicUser | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  login: (email: string, password: string, mode?: LoginMode) => Promise<PublicUser>;
  logout: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const qc = useQueryClient();

  const meQuery = useQuery(meQueryOptions);

  const login = useCallback(
    async (email: string, password: string, mode: LoginMode = 'local') => {
      const res =
        mode === 'ldap'
          ? await api.loginLdap({ email, password })
          : await api.login({ email, password });
      qc.setQueryData(['auth', 'me'], res.user);
      return res.user;
    },
    [qc],
  );

  const logout = useCallback(async () => {
    const res = await api.logout();
    qc.setQueryData(['auth', 'me'], null);
    qc.removeQueries({ queryKey: ['auth'] });
    // SSO provider 가 자체 로그아웃 URL 을 돌려준 경우 (예: ISign+) — 외부 IdP 세션도
    // 같이 만료시키기 위해 브라우저를 그 페이지로 보낸다. 그 외에는 SPA 내부에서 로그인
    // 페이지로 자연 이동 (useEffect 가 isAuthenticated 변화 감지).
    if (res.redirectUrl) {
      window.location.href = res.redirectUrl;
    }
  }, [qc]);

  const value = useMemo<AuthContextValue>(
    () => ({
      user: meQuery.data ?? null,
      isLoading: meQuery.isLoading,
      isAuthenticated: !!meQuery.data,
      login,
      logout,
    }),
    [meQuery.data, meQuery.isLoading, login, logout],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
